import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users, Target, CalendarCheck, Activity,
  Swords, ShieldAlert, Dumbbell, ChevronRight,
  GraduationCap, Wallet, FileText, MessageSquare, Clock,
  AlertTriangle, CheckCircle2, BarChart3, UserCog, Route, Flag,
  Brain, CheckSquare
} from 'lucide-react';
import LatestCoachFeedback from '../components/LatestCoachFeedback';
import AdminDigest from '../components/AdminDigest';
import InjuredThisWeek from '../components/InjuredThisWeek';
import CurrentSeasonPill from '../components/CurrentSeasonPill';
import QuickActionsBar from '../components/QuickActionsBar';
import TodayStatusCard from '../components/TodayStatusCard';
import DashboardAcademicAttention from '../components/academic/DashboardAcademicAttention';
import { lastUpdated } from '../utils/relativeTime';

const API = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [coachToday, setCoachToday] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const canAccess = (mod) => hasModuleAccess(mod, 'view');

  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const isStaffRole = user?.role === 'Admin' || user?.role === 'Staff';
      const calls = [
        fetch(`${API}/api/dashboard/kpis`, { headers }),
        fetch(`${API}/api/announcements`, { headers }),
        fetch(`${API}/api/announcements/unread-count`, { headers }),
      ];
      if (isStaffRole) calls.push(fetch(`${API}/api/dashboard/coach-today`, { headers }));
      const responses = await Promise.all(calls);
      const [kpiRes, annRes, unreadRes, coachRes] = responses;
      if (kpiRes.ok) setKpis(await kpiRes.json());
      if (annRes.ok) {
        const data = await annRes.json();
        setAnnouncements(data.slice(0, 3));
      }
      if (unreadRes.ok) {
        const data = await unreadRes.json();
        setUnreadCount(data.unread_count || 0);
      }
      if (coachRes && coachRes.ok) setCoachToday(await coachRes.json());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, user?.role]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';
  const trainingRoute = isStaff ? '/training' : '/my-training';
  const announcementRoute = isStaff ? '/announcements' : '/my-announcements';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const getPriorityColor = (p) => {
    const map = { Urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400', Important: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' };
    return map[p] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen" data-testid="dashboard-loading">
          <div className="h-16 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse mb-6" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-48 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
          </div>
        </div>
      </Layout>
    );
  }

  const k = kpis || {};

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 min-h-screen" data-testid="dashboard">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading uppercase text-pfap-green dark:text-gray-100" data-testid="dashboard-greeting">
              Welcome back, {user?.full_name}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-body">
              {isStaff ? 'PFAP Hub — Academy Management Dashboard' : user?.role === 'Parent' ? 'PFAP Hub — Parent Portal' : 'PFAP Hub — Athlete Portal'}
            </p>
          </div>
          <Badge className={`ml-auto text-xs px-2.5 py-1 ${getRoleBadgeStyle(user?.role)}`} data-testid="user-role-badge">
            {user?.role}
          </Badge>
        </div>

        {/* Current season pill — Admin only */}
        {user?.role === 'Admin' && (
          <div className="mb-4">
            <CurrentSeasonPill />
          </div>
        )}

        {/* Player / Parent — pinned "Today's Status" card: availability, next training,
            latest match, injury status. All read-only, from existing endpoints. */}
        {!isStaff && (
          <TodayStatusCard role={user?.role} />
        )}

        {/* Coach Today — single daily decision surface (Admin + Staff only) */}
        {isStaff && coachToday && (
          <CoachTodayPanel data={coachToday} onNavigate={navigate} />
        )}

        {/* Phase 72.C — small academic-attention chip row. Admin + Staff only.
            The component returns null when there are no signals to surface. */}
        {isStaff && (
          <div className="mb-4">
            <DashboardAcademicAttention />
          </div>
        )}

        {/* Quick Actions — Staff/Admin daily drivers, 1 click from dashboard */}
        {isStaff && (
          <QuickActionsBar hasToday={(coachToday?.today_sessions?.length || 0) > 0} />
        )}

        {isStaff && (
          <div className="mb-6 space-y-3">
            <InjuredThisWeek />
            {user?.role === 'Admin' && <AdminDigest />}
          </div>
        )}

        {/* KPI Cards — Hero KPI (first card) is visually dominant (2× width on desktop,
            larger value typography); remaining 3 are supportive. */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="kpi-grid">
          <KPICard
            hero
            icon={Users}
            label={isStaff ? 'Fit Athletes' : 'Squad Status'}
            value={k.fit_count ?? 0}
            subtitle={`${k.injured_count ?? 0} injured · ${k.total_athletes ?? 0} total`}
            color="emerald"
            onClick={() => navigate(isStaff ? (user?.role === 'Admin' ? '/athletes' : '/my-athletes') : '/my-profile')}
            testId="kpi-fit-athletes"
          />
          {canAccess('pdp') && (
            <KPICard
              icon={Target}
              label="Avg PDP Progress"
              value={`${k.avg_pdp_progress ?? 0}%`}
              subtitle={`${k.athletes_with_pdp ?? 0} athletes with active PDP`}
              color="purple"
              onClick={() => navigate('/pdp')}
              testId="kpi-pdp-progress"
            />
          )}
          {canAccess('training') && (
            <KPICard
              icon={CalendarCheck}
              label="Attendance Rate"
              value={`${k.attendance_pct ?? 0}%`}
              subtitle={`${k.present_records ?? 0} present · ${k.absent_records ?? 0} absent`}
              color="blue"
              onClick={() => navigate(trainingRoute)}
              testId="kpi-attendance"
            />
          )}
          {isStaff && canAccess('injury_recovery') ? (
            <KPICard
              icon={ShieldAlert}
              label="Injury Alerts"
              value={k.injured_count ?? 0}
              subtitle={k.injured_count > 0 ? 'Athletes currently injured' : 'All athletes fit'}
              color={k.injured_count > 0 ? 'red' : 'emerald'}
              onClick={() => navigate('/injury-recovery')}
              testId="kpi-injuries"
            />
          ) : (
            !canAccess('pdp') || !canAccess('training') ? (
              <KPICard
                icon={Users}
                label="Total Athletes"
                value={k.total_athletes ?? 0}
                subtitle={`${k.injured_count ?? 0} injured · ${k.fit_count ?? 0} fit`}
                color="blue"
                onClick={() => navigate(isStaff ? '/my-athletes' : '/my-profile')}
                testId="kpi-total-athletes"
              />
            ) : null
          )}
        </div>

        {/* Player / Parent — latest coach feedback surfaced here so they don't have to drill. */}
        {!isStaff && (
          <div className="mb-6">
            <LatestCoachFeedback />
          </div>
        )}

        {/* Main Content: 3 column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Upcoming Sessions */}
          {canAccess('training') && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="upcoming-sessions-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Dumbbell size={16} className="text-blue-500" /> Upcoming Sessions
                </CardTitle>
                <button onClick={() => navigate(trainingRoute)} className="text-xs text-pfap-green dark:text-gray-400 hover:text-pfap-green/80 dark:hover:text-pfap-sage font-medium flex items-center gap-0.5" data-testid="view-all-sessions-btn">
                  View All <ChevronRight size={14} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {(k.upcoming_sessions?.length || 0) === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">No upcoming sessions</p>
              ) : (
                <div className="space-y-2">
                  {k.upcoming_sessions.map((s, i) => (
                    <div
                      key={s.id || i}
                      onClick={() => navigate(trainingRoute)}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border border-gray-50 dark:border-gray-700/50 cursor-pointer"
                      data-testid={`session-item-${i}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                        <Clock size={16} className="text-blue-500 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{s.title || 'Training Session'}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{formatDate(s.date)} {s.type ? `· ${s.type}` : ''}</p>
                      </div>
                      {s.duration_minutes && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 px-1.5 py-0.5 rounded">{s.duration_minutes}m</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Recent Match Reports */}
          {canAccess('match_reports') && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="recent-matches-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Swords size={16} className="text-purple-500" /> Recent Matches
                </CardTitle>
                <button onClick={() => navigate('/match-reports')} className="text-xs text-pfap-green dark:text-gray-400 hover:text-pfap-green/80 dark:hover:text-pfap-sage font-medium flex items-center gap-0.5" data-testid="view-all-matches-btn">
                  View All <ChevronRight size={14} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {(k.recent_matches?.length || 0) === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">No match reports yet</p>
              ) : (
                <div className="space-y-2">
                  {k.recent_matches.map((m, i) => (
                    <div key={m.id || i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border border-gray-50 dark:border-gray-700/50 cursor-pointer" onClick={() => navigate('/match-reports')} data-testid={`match-item-${i}`}>
                      <div className="w-9 h-9 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <Swords size={16} className="text-purple-500 dark:text-purple-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">vs {m.opponent || 'Unknown'}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{m.athlete_name} · {formatDate(m.match_date)}</p>
                      </div>
                      {m.competition && (
                        <Badge className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-0">{m.competition}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Announcements */}
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="announcements-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <MessageSquare size={16} className="text-amber-500" /> Announcements
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">{unreadCount}</Badge>
                  )}
                </CardTitle>
                <button onClick={() => navigate(announcementRoute)} className="text-xs text-pfap-green dark:text-gray-400 hover:text-pfap-green/80 dark:hover:text-pfap-sage font-medium flex items-center gap-0.5" data-testid="view-all-announcements-btn">
                  View All <ChevronRight size={14} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {announcements.length === 0 ? (
                <div className="py-6 text-center" data-testid="announcements-empty">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isStaff ? 'No announcements yet — share an update with your squad.' : "Nothing to read. You're all caught up."}
                  </p>
                  {isStaff && (
                    <button
                      onClick={() => navigate('/announcements')}
                      className="mt-2 text-xs text-pfap-green hover:underline font-medium"
                      data-testid="announcements-empty-cta"
                    >
                      + Create announcement
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {announcements.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => navigate(announcementRoute)}
                      className={`p-2.5 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                        !a.is_read ? 'border-l-[3px] border-l-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10' : 'border-gray-50 dark:border-gray-700/50'
                      }`}
                      data-testid={`announcement-${a.id}`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{a.title}</span>
                        {a.priority && a.priority !== 'Normal' && (
                          <Badge className={`text-[10px] px-1 py-0 ${getPriorityColor(a.priority)}`}>{a.priority}</Badge>
                        )}
                        {!a.is_read && <Badge className="bg-emerald-500 text-white text-[10px] px-1 py-0">New</Badge>}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{a.message}</p>
                      {a.created_at ? (
                        <p className="text-[10px] text-gray-400 mt-1" data-testid={`announcement-${a.id}-time`}>
                          {lastUpdated(a.created_at, 'Posted')}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row: Alerts + Squad Breakdown + Quick Access */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Active Alerts (Flags) — only for Staff/Admin */}
          {isStaff && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="alerts-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Flag size={16} className="text-red-500" /> Active Alerts
                  {(k.active_flags_count || 0) > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">{k.active_flags_count}</Badge>
                  )}
                </CardTitle>
                {isStaff && (
                  <button onClick={() => navigate('/alerts-flags')} className="text-xs text-pfap-green dark:text-gray-400 hover:text-pfap-green/80 dark:hover:text-pfap-sage font-medium flex items-center gap-0.5" data-testid="view-all-flags-btn">
                    View All <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {(k.active_flags?.length || 0) === 0 && (k.injured_athletes?.length || 0) === 0 ? (
                <div className="flex items-center gap-2 py-4 justify-center">
                  <CheckCircle2 size={16} className="text-emerald-500" />
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">No active alerts</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {(k.active_flags || []).slice(0, 4).map((f, i) => {
                    const sevColor = f.severity === 'High' ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/40' : f.severity === 'Medium' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/40' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/40';
                    const sevBadge = f.severity === 'High' ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/40' : f.severity === 'Medium' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800/40' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800/40';
                    return (
                      <div
                        key={f.id || i}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer hover:opacity-80 transition-all ${sevColor}`}
                        onClick={() => navigate('/alerts-flags')}
                        data-testid={`alert-flag-${i}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-white/60 dark:bg-gray-700/60 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle size={14} className={f.severity === 'High' ? 'text-red-500' : f.severity === 'Medium' ? 'text-amber-500' : 'text-blue-500'} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{f.athlete_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{f.flag_type}</p>
                        </div>
                        <Badge className={`text-[10px] border ${sevBadge}`}>{f.severity}</Badge>
                      </div>
                    );
                  })}
                  {(k.injured_athletes || []).filter(a => !(k.active_flags || []).some(f => f.athlete_id === a.id)).slice(0, 2).map((a, i) => (
                    <div
                      key={a.id || i}
                      className="flex items-center gap-3 p-2.5 rounded-lg bg-red-50/50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      onClick={() => navigate('/injury-recovery')}
                      data-testid={`alert-injury-${i}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                        <ShieldAlert size={14} className="text-red-500 dark:text-red-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{a.full_name}</p>
                        <p className="text-xs text-red-500 dark:text-red-400">{a.position} · Injured</p>
                      </div>
                      <Badge className="text-[10px] bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/40">Injury</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Squad Breakdown — Staff/Admin only */}
          {isStaff && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="squad-breakdown-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <BarChart3 size={16} className="text-blue-500" /> Squad Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {Object.keys(k.squad_counts || {}).length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">No squad data</p>
              ) : (
                <div className="space-y-3 py-1">
                  {Object.entries(k.squad_counts || {}).sort((a, b) => b[1] - a[1]).map(([squad, count]) => {
                    const pct = k.total_athletes > 0 ? Math.round((count / k.total_athletes) * 100) : 0;
                    const colors = getSquadColor(squad);
                    return (
                      <div key={squad} data-testid={`squad-${squad}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{squad}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">{count} athletes ({pct}%)</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-500 ${colors}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Quick Access */}
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="quick-access-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Activity size={16} className="text-emerald-500" /> Quick Access
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-2">
                {getQuickAccessItems(user?.role, canAccess).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-pfap-sage/50 dark:hover:border-pfap-sage/30 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all group"
                    data-testid={`quick-access-${item.id}`}
                  >
                    <item.icon size={18} className={`${item.iconColor} group-hover:scale-110 transition-transform`} />
                    <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400 text-center leading-tight">{item.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

/* ==================== Helpers ==================== */

const KPICard = ({ icon: Icon, label, value, subtitle, color, onClick, testId, hero = false }) => {
  const styles = {
    emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', accentBar: 'bg-emerald-500' },
    purple:  { bg: 'bg-purple-50 dark:bg-purple-900/30',   icon: 'text-purple-600 dark:text-purple-400',   accentBar: 'bg-purple-500' },
    blue:    { bg: 'bg-blue-50 dark:bg-blue-900/30',       icon: 'text-blue-600 dark:text-blue-400',       accentBar: 'bg-blue-500' },
    red:     { bg: 'bg-red-50 dark:bg-red-900/30',         icon: 'text-red-600 dark:text-red-400',         accentBar: 'bg-red-500' },
    amber:   { bg: 'bg-amber-50 dark:bg-amber-900/30',     icon: 'text-amber-600 dark:text-amber-400',     accentBar: 'bg-amber-500' },
  };
  const s = styles[color] || styles.emerald;

  // Hero variant takes 2 columns on desktop and on mobile spans the full row.
  // Bigger icon, bigger number, richer presence.
  const span = hero ? 'col-span-2 lg:col-span-2' : 'lg:col-span-1';

  return (
    <Card
      className={`relative overflow-hidden bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md hover:border-pfap-sage/40 dark:hover:border-pfap-sage/30 transition-all group ${span}`}
      onClick={onClick}
      data-testid={testId}
    >
      {/* Accent stripe on hero for visual dominance */}
      {hero && (
        <span className={`absolute left-0 top-0 bottom-0 w-[4px] ${s.accentBar}`} aria-hidden="true" />
      )}
      <CardContent className={hero ? 'p-5' : 'p-4'}>
        <div className="flex items-start justify-between mb-3">
          <div className={`${hero ? 'w-11 h-11' : 'w-9 h-9'} rounded-lg ${s.bg} flex items-center justify-center`}>
            <Icon size={hero ? 22 : 18} className={s.icon} />
          </div>
          <ChevronRight size={14} className="text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors mt-1" />
        </div>
        <p
          className={`${hero ? 'text-4xl sm:text-5xl' : 'text-3xl'} font-bold text-gray-900 dark:text-gray-100 leading-none mb-1 tracking-tight`}
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid={`${testId}-value`}
        >
          {value}
        </p>
        <p className={`${hero ? 'text-sm' : 'text-xs'} font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-0.5`}>{label}</p>
        <p className={`${hero ? 'text-xs' : 'text-[11px]'} text-gray-400 dark:text-gray-500`}>{subtitle}</p>
      </CardContent>
    </Card>
  );
};

const getRoleBadgeStyle = (role) => {
  const map = {
    Admin: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800',
    Staff: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
    Player: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
    Parent: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  };
  return map[role] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const getSquadColor = (squad) => {
  const map = { U16: 'bg-blue-400', U17: 'bg-sky-400', U19: 'bg-emerald-400', Senior: 'bg-purple-400', Unassigned: 'bg-gray-400' };
  return map[squad] || 'bg-gray-400';
};

const getQuickAccessItems = (role, canAccess) => {
  if (role === 'Admin') {
    return [
      { id: 'athletes', label: 'Athletes', icon: Users, iconColor: 'text-emerald-500', path: '/athletes' },
      { id: 'training', label: 'Training', icon: Dumbbell, iconColor: 'text-blue-500', path: '/training' },
      { id: 'flags', label: 'Alerts', icon: Flag, iconColor: 'text-red-500', path: '/alerts-flags' },
      { id: 'matches', label: 'Matches', icon: Swords, iconColor: 'text-purple-500', path: '/match-reports' },
      { id: 'injury', label: 'Injuries', icon: ShieldAlert, iconColor: 'text-orange-500', path: '/injury-recovery' },
      { id: 'pdp', label: 'PDP', icon: Target, iconColor: 'text-violet-500', path: '/pdp' },
      { id: 'academic', label: 'Academic', icon: GraduationCap, iconColor: 'text-amber-500', path: '/academic' },
      { id: 'finance', label: 'Finance', icon: Wallet, iconColor: 'text-teal-500', path: '/finance' },
      { id: 'career', label: 'Career', icon: Route, iconColor: 'text-sky-500', path: '/career-pathway' },
    ];
  }
  if (role === 'Staff') {
    const items = [
      { id: 'athletes', label: 'Athletes', icon: Users, iconColor: 'text-emerald-500', path: '/my-athletes' },
    ];
    if (canAccess('training')) items.push({ id: 'training', label: 'Training', icon: Dumbbell, iconColor: 'text-blue-500', path: '/training' });
    items.push({ id: 'flags', label: 'Alerts', icon: Flag, iconColor: 'text-red-500', path: '/alerts-flags' });
    if (canAccess('match_reports')) items.push({ id: 'matches', label: 'Matches', icon: Swords, iconColor: 'text-purple-500', path: '/match-reports' });
    if (canAccess('injury_recovery')) items.push({ id: 'injury', label: 'Injuries', icon: ShieldAlert, iconColor: 'text-orange-500', path: '/injury-recovery' });
    if (canAccess('pdp')) items.push({ id: 'pdp', label: 'PDP', icon: Target, iconColor: 'text-violet-500', path: '/pdp' });
    if (canAccess('academic')) items.push({ id: 'academic', label: 'Academic', icon: GraduationCap, iconColor: 'text-amber-500', path: '/academic' });
    if (canAccess('finance')) items.push({ id: 'finance', label: 'Finance', icon: Wallet, iconColor: 'text-teal-500', path: '/finance' });
    if (canAccess('career_pathway')) items.push({ id: 'career', label: 'Career', icon: Route, iconColor: 'text-sky-500', path: '/career-pathway' });
    return items;
  }
  // Player / Parent — only show modules they have permission to access
  const items = [
    { id: 'profile', label: 'My Profile', icon: Users, iconColor: 'text-emerald-500', path: '/my-profile' },
  ];
  if (canAccess('training')) items.push({ id: 'training', label: 'Training', icon: Dumbbell, iconColor: 'text-blue-500', path: '/my-training' });
  if (canAccess('match_reports')) items.push({ id: 'performance', label: 'Performance', icon: Swords, iconColor: 'text-purple-500', path: '/my-performance' });
  if (canAccess('pdp')) items.push({ id: 'pdp', label: 'PDP', icon: Target, iconColor: 'text-violet-500', path: '/pdp' });
  if (canAccess('assessments')) items.push({ id: 'assessments', label: 'Assessments', icon: CheckSquare, iconColor: 'text-blue-500', path: '/my-assessments' });
  if (canAccess('academic')) items.push({ id: 'academic', label: 'Academic', icon: GraduationCap, iconColor: 'text-amber-500', path: '/my-academic' });
  if (canAccess('finance')) items.push({ id: 'finance', label: 'Finance', icon: Wallet, iconColor: 'text-orange-500', path: '/my-finance' });
  if (canAccess('documents')) items.push({ id: 'documents', label: 'Documents', icon: FileText, iconColor: 'text-sky-500', path: '/my-documents' });
  if (canAccess('announcements')) items.push({ id: 'announcements', label: 'News', icon: MessageSquare, iconColor: 'text-amber-500', path: '/my-announcements' });
  if (canAccess('career_pathway')) items.push({ id: 'career', label: 'Career', icon: Route, iconColor: 'text-sky-500', path: '/career-pathway' });
  // Mental Hub: Player always has access to athlete space, Parent sees parent insights
  if (role === 'Player') items.push({ id: 'mental-hub', label: 'Mental Hub', icon: Brain, iconColor: 'text-violet-500', path: '/mental-hub' });
  if (role === 'Parent') items.push({ id: 'parent-insights', label: 'Parent Insights', icon: Brain, iconColor: 'text-violet-500', path: '/mental-hub' });
  // Programs (S&C / Nutrition) — accessible to all Player/Parent
  items.push({ id: 'programs', label: 'Programs', icon: Activity, iconColor: 'text-teal-500', path: '/performance-programs' });
  return items;
};

export default Dashboard;

// =============================================================================
// Coach Today Panel — unified daily decision surface (Admin + Staff)
// =============================================================================

const CoachTodayPanel = ({ data, onNavigate }) => {
  const av = data?.availability || {};
  const sessions = data?.today_sessions || [];
  const attention = data?.attention || [];

  const reasonBadgeClass = (reason) => {
    if (reason.startsWith('Injured')) return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
    if (reason.startsWith('Low wellness') || reason.startsWith('Wellness')) return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800';
    if (reason.startsWith('Missed')) return 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
    if (reason.startsWith('PDP')) return 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800';
    return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  };

  return (
    <div className="mb-6" data-testid="coach-today-panel">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Today · {new Date(data.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
        </h2>
        <span className="text-[11px] text-gray-400">live · canonical data only</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Availability */}
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4" data-testid="coach-availability">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <Users size={16} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Availability</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Active" value={av.total_active ?? 0} color="text-gray-900 dark:text-gray-100" testId="coach-stat-active" />
            <StatTile label="Available" value={av.available ?? 0} color="text-emerald-600 dark:text-emerald-400" testId="coach-stat-available" />
            <StatTile label="Injured" value={av.injured ?? 0} color={av.injured > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'} testId="coach-stat-injured" />
            <StatTile label="No wellness" value={av.no_wellness_today ?? 0} color={av.no_wellness_today > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'} testId="coach-stat-no-wellness" />
          </div>
        </div>

        {/* Today Sessions */}
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4" data-testid="coach-today-sessions">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                <Dumbbell size={16} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Today's Training</h3>
            </div>
            <button onClick={() => onNavigate('/training')} className="text-[11px] text-pfap-green hover:underline" data-testid="coach-sessions-view-all">
              Open planner →
            </button>
          </div>
          {sessions.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-xs text-gray-400 dark:text-gray-500">No session planned today.</p>
              <button onClick={() => onNavigate('/training')} className="text-xs text-pfap-green hover:underline mt-1" data-testid="coach-create-session">
                + Schedule a session
              </button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="coach-session-list">
              {sessions.slice(0, 4).map(s => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => onNavigate('/training')}
                  data-testid={`coach-session-${s.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <Clock size={14} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{s.title}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {String(s.time || '').slice(0, 5)}{s.end_time ? `–${String(s.end_time).slice(0, 5)}` : ''} · {s.session_type} · {s.target_athlete_count} athletes
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attention list */}
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4" data-testid="coach-attention">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle size={16} className="text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Needs Attention</h3>
            </div>
            <Badge className="text-[10px] px-1.5 py-0 bg-red-50 text-red-700 border-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800" data-testid="coach-attention-count">
              {attention.length}
            </Badge>
          </div>
          {attention.length === 0 ? (
            <div className="py-6 text-center">
              <CheckCircle2 size={20} className="mx-auto text-emerald-500 mb-1" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Squad is settled. Nothing needs attention.</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1" data-testid="coach-attention-list">
              {attention.slice(0, 8).map(item => (
                <AttentionRow key={item.athlete_id} item={item} reasonBadgeClass={reasonBadgeClass} onNavigate={onNavigate} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, color, testId }) => (
  <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2" data-testid={testId}>
    <p className={`text-2xl font-bold ${color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mt-0.5">{label}</p>
  </div>
);

// =============================================================================
// Attention row — inline quick-nav to 360 / Training / Injury / PDP
// Reduces clicks: instead of opening 360 and navigating modules from there,
// coaches jump straight to the relevant module for the flagged athlete.
// =============================================================================
const AttentionRow = ({ item, reasonBadgeClass, onNavigate }) => {
  const aid = item.athlete_id;
  const go = (e, path) => {
    e.stopPropagation();
    onNavigate(path);
  };
  return (
    <div
      className="group flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
      onClick={() => onNavigate(`/athlete/${aid}/360`)}
      data-testid={`coach-attention-${aid}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onNavigate(`/athlete/${aid}/360`);
      }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {item.athlete_name}
          <span className="text-[10px] text-gray-400 ml-1.5 font-normal">· {item.squad}</span>
        </p>
        <div className="flex flex-wrap gap-1 mt-1">
          {item.reasons.slice(0, 3).map((r, idx) => (
            <span key={idx} className={`inline-block text-[9px] px-1.5 py-0 rounded-full border ${reasonBadgeClass(r)}`}>{r}</span>
          ))}
          {item.reasons.length > 3 && (
            <span className="text-[9px] text-gray-400">+{item.reasons.length - 3}</span>
          )}
        </div>
      </div>
      {/* Inline quick-jump chips — visible on hover, keyboard accessible */}
      <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
        <QuickJumpChip
          icon={Dumbbell}
          label="Training"
          onClick={(e) => go(e, `/training?athlete=${aid}`)}
          testId={`attention-jump-training-${aid}`}
        />
        <QuickJumpChip
          icon={ShieldAlert}
          label="Injury"
          onClick={(e) => go(e, `/injury-recovery?athlete=${aid}`)}
          testId={`attention-jump-injury-${aid}`}
        />
        <QuickJumpChip
          icon={Target}
          label="PDP"
          onClick={(e) => go(e, `/pdp?athlete=${aid}`)}
          testId={`attention-jump-pdp-${aid}`}
        />
        <ChevronRight size={14} className="text-gray-300 flex-shrink-0 ml-0.5" />
      </div>
    </div>
  );
};

const QuickJumpChip = ({ icon: Icon, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="p-1 rounded hover:bg-white dark:hover:bg-gray-800 hover:shadow-sm text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all"
    data-testid={testId}
  >
    <Icon size={12} />
  </button>
);
